/* @fflow */
import { handleActions } from 'redux-actions';
import { fetchGraphql } from '../utils/ajax';

export const LIST_REQUEST = 'gdc/LIST_REQUEST';
export const LIST_SUCCESS = 'gdc/LIST_SUCCESS';
export const LIST_FAILURE = 'gdc/LIST_FAILURE';

function entitySpecificQuery(entity, submittedFileTypes, biospecimenFileTypes) {
  const linksQuery = `
    _links {
      id
      type
      ${biospecimenFileTypes.map(d => `...on ${d} { submitter_id }`).join()}
      ${submittedFileTypes.map(d => `...on ${d} { submitter_id }`).join()}
      ...on annotation {
        submitter_id
      }
    }
  `;

  switch (entity) {
    case 'annotation':
      return linksQuery;
    default:
      return '';
  }
}

function queryEntities({
  first, offset, program, project, id, order, sort, quickSearch, entity, fields, fileState,
  withLinks, withLinksAny, withoutLinks, withPathTo, withoutPathTo, withPathToAny,
}, submittedFileTypes, biospecimenFileTypes) {
  const filterParams = `
    id: $id
    project_id: $projectId
    quick_search: $quickSearch
    with_path_to: $withPathTo
    with_path_to_any: $withPathToAny
    with_links: $withLinks
    with_links_any: $withLinksAny
    without_links: $withoutLinks
    without_path_to: $withoutPathTo
    ${entity === 'submitted_file' ? 'file_state: $fileState' : ''}
  `;

  return {
    variables: {
      quickSearch,
      first,
      offset,
      fileState: fileState ? fileState.split(',') : null,
      projectId: project ? `${program}-${project}` : null,
      id: id ? `${id}` : null,
      orderByAsc: order === 'asc' ? sort : null,
      orderByDesc: order === 'desc' ? sort : null,
      withLinks: withLinks ? withLinks.split(',') : [],
      withLinksAny: withLinksAny ? withLinksAny.split(',') : null,
      withoutLinks: withoutLinks ? withoutLinks.split(',') : [],
      withPathTo: withPathTo || null,
      withPathToAny: withPathToAny ? withPathToAny.split(',').map(x => ({ type: x })) : null,
      withoutPathTo: withoutPathTo ? withoutPathTo.split(',').map(x => ({ type: x })) : null,
    },
    query: `
      query DefaultQuery(
        $first: Int
        $offset: Int
        $projectId: [String]
        $quickSearch: String
        $id: String
        $orderByAsc: String
        $orderByDesc: String
        $withPathTo: [WithPathToInput]
        $withPathToAny: [WithPathToInput]
        $withLinks: [String]
        $withLinksAny: [String]
        $withoutLinks: [String]
        $withoutPathTo: [WithPathToInput]
        ${entity === 'submitted_file' ? '$fileState: [String]' : ''}
      ) {
        count: _${entity}_count(${filterParams})
        items: ${entity}(
          order_by_asc: $orderByAsc
          order_by_desc: $orderByDesc
          first: $first
          offset: $offset
          ${filterParams}
        ) {
          ${entitySpecificQuery(entity, submittedFileTypes, biospecimenFileTypes)}
          ${fields.join()}
          ${entity !== 'case' ? '_related_cases { id submitter_id }' : ''}
        }
      }
    `,
  };
}

function queryTransactions({
  quickSearch, first, offset, sort, order, program, project, id, state, type, isDryRun, committable, closed,
}) {
  return {
    variables: {
      quickSearch,
      first,
      offset,
      projectId: project ? `${program}-${project}` : null,
      id,
      orderByAsc: order === 'asc' ? sort : null,
      orderByDesc: order === 'desc' ? sort : null,
      state: state || null,
      type: type || null,
      isDryRun: isDryRun ? isDryRun === 'true' : null,
      committable: committable ? committable === 'true' : null,
      closed: closed ? closed === 'true' : null,
    },
    query:
      `query TransactionsQuery(
        $first: Int
        $offset: Int
        $projectId: [String]
        $id: ID
        $orderByAsc: String
        $orderByDesc: String
        $quickSearch: ID
        $type: String
        $isDryRun: Boolean
        $state: String
        $committable: Boolean
        $closed: Boolean
      ) {
        count: _transaction_log_count(
          id: $id
          quick_search: $quickSearch
          project_id: $projectId
          type: $type
          state: $state
          is_dry_run: $isDryRun
          committable: $committable
          closed: $closed
        )
        items: transaction_log(
          id: $id
          quick_search: $quickSearch
          project_id: $projectId
          order_by_asc: $orderByAsc
          order_by_desc: $orderByDesc
          first: $first
          offset: $offset
          type: $type
          state: $state
          is_dry_run: $isDryRun
          committable: $committable
          closed: $closed
        ) {
          id
          project_id
          type
          created_datetime
          state
          is_dry_run
          committed_by
          submitter
          closed
        }
      }`,
  };
}

export const fetchTransactions = variables => (
  fetchGraphql(
    [
      { type: LIST_REQUEST, meta: { entity: 'transaction' } },
      { type: LIST_SUCCESS, meta: { entity: 'transaction' } }, LIST_FAILURE,
    ],
    queryTransactions(variables),
  )
);

export const fetchEntities = (variables, submittedFileTypes, biospecimenFileTypes) => (
  fetchGraphql(
    [
      { type: LIST_REQUEST, meta: { entity: variables.entity } },
      { type: LIST_SUCCESS, meta: { entity: variables.entity } }, LIST_FAILURE,
    ],
    queryEntities(variables, submittedFileTypes, biospecimenFileTypes),
  )
);

const initialState = {
  isFetching: false,
  entity: '',
  data: {
    count: 0,
    items: [],
  },
};

export default handleActions({
  [LIST_REQUEST]: (state, action) => ({
    ...state,
    entity: action.meta.entity,
    isFetching: true,
    data: action.meta.entity === state.entity ? state.data : {},
  }),
  [LIST_SUCCESS]: (state, action) => ({
    ...state,
    isFetching: false,
    data: action.meta.entity === state.entity ? action.payload.data : state.data,
  }),
  [LIST_FAILURE]: state => ({
    ...state,
    isFetching: false,
  }),
}, initialState);
